/**
 * Quick View
 * ----------
 * Card click → PDP (default e‑commerce behaviour, better SEO + research).
 * Quick View is triggered ONLY from a small eye-icon overlay button that
 * this script injects into each product-card's gallery on page load.
 *
 * The Quick View ATC delegates to Horizon's `<product-form-component>`
 * (returned in the fetched section HTML), so the cart drawer refreshes
 * and opens with the just-added item — no empty-drawer flash.
 */

(() => {
  // Don't interfere with the Theme Editor.
  if (window.Shopify && window.Shopify.designMode) return;

  const CARD_SELECTOR = 'product-card';
  const PRODUCT_LINK_SELECTOR = 'a[href*="/products/"]';
  const CONTENT_SELECTOR = '.qv-content';
  const TRIGGER_ATTR = 'data-qv-injected';
  const cache = new Map();

  /** Storefront route root (includes /<locale>/ prefix when active). */
  const routeRoot = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /** Extract product handle from a URL like /products/divain-242?variant=… */
  const handleFromUrl = (url) => {
    try {
      const u = new URL(url, window.location.origin);
      const parts = u.pathname.split('/').filter(Boolean);
      const idx = parts.indexOf('products');
      if (idx !== -1 && parts[idx + 1]) return parts[idx + 1];
    } catch (_) {}
    return null;
  };

  /** Build the eye-icon Quick View button.
   *  Structure mirrors the add-to-cart-button snippet so the hover expansion
   *  CSS (grid 0fr→1fr + blur/translate fade) can reuse the same technique. */
  const buildIconButton = (handle, productTitle) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'qv-trigger';
    btn.dataset.qvHandle = handle;
    btn.setAttribute('aria-label', `Vista rápida ${productTitle || ''}`.trim());
    btn.innerHTML = `
      <span class="qv-trigger__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
      </span>
      <span class="qv-trigger__label"><span><span>Vista rápida</span></span></span>
    `;
    return btn;
  };

  /** Inject the eye-icon button into a single product card (if not already done). */
  const injectIntoCard = (card) => {
    if (!card || card.hasAttribute(TRIGGER_ATTR)) return;

    // Get product handle from any product link inside the card.
    const link = card.querySelector(PRODUCT_LINK_SELECTOR);
    const handle = link ? handleFromUrl(link.getAttribute('href')) : null;
    if (!handle) return;

    // Prefer placing the trigger INSIDE the quick-add element so it inherits
    // the same --quick-add-* CSS vars used to position the quick-add button.
    // That keeps both buttons perfectly aligned at the bottom of the card.
    // Fall back to the gallery container, then to the card itself.
    const host =
      card.querySelector('quick-add-component.quick-add') ||
      card.querySelector('.quick-add') ||
      card.querySelector('.card-gallery') ||
      card.querySelector('[ref="cardGallery"]') ||
      card.querySelector('.product-card__content') ||
      card;

    // Make sure the host can position the button absolutely. Skip hosts that
    // already have explicit positioning in the theme CSS (.quick-add is
    // `absolute; inset: 0` — overriding it breaks the card layout) and defer
    // the computed-style read into rAF so we don't trigger sync reflow per card.
    if (!host.matches('quick-add-component, .quick-add, .card-gallery, [ref="cardGallery"]')) {
      requestAnimationFrame(() => {
        if (window.getComputedStyle(host).position === 'static') {
          host.style.position = 'relative';
        }
      });
    }

    const title = card.querySelector('.product-title, .product-card__title')?.textContent?.trim() || '';
    const btn = buildIconButton(handle, title);
    host.appendChild(btn);

    card.setAttribute(TRIGGER_ATTR, '');
  };

  /** Inject the button into every product-card currently in the DOM. */
  const injectAll = () => {
    document.querySelectorAll(CARD_SELECTOR).forEach(injectIntoCard);
  };

  /** Watch for newly added product cards (infinite scroll, AJAX filters, etc.). */
  const observeNewCards = () => {
    const obs = new MutationObserver((records) => {
      for (const r of records) {
        for (const node of r.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          if (node.matches?.(CARD_SELECTOR)) injectIntoCard(node);
          node.querySelectorAll?.(CARD_SELECTOR).forEach(injectIntoCard);
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  };

  class QuickViewDialog extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;

      this.body = this.dialog.querySelector('[data-qv-body]');

      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.close();
      });

      this.dialog.addEventListener('click', (e) => {
        if (e.target.closest('[data-qv-close]')) {
          e.preventDefault();
          this.close();
        }
      });

      this.dialog.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-qv-thumb-index]');
        if (thumb) this.switchSlide(parseInt(thumb.dataset.qvThumbIndex, 10));
      });

      this.dialog.addEventListener('close', () => this.reset());

      // Close the modal once Horizon confirms the item was added to the cart.
      // Horizon's product-form-component dispatches these as a bubbling event.
      ['cart:item-added', 'cart:refresh', 'cart-update'].forEach((name) => {
        document.addEventListener(name, () => {
          if (this.dialog.open) this.close();
        });
      });
    }

    switchSlide(index) {
      if (!Number.isFinite(index)) return;
      const slides = this.body.querySelectorAll('[data-qv-slide-index]');
      const thumbs = this.body.querySelectorAll('[data-qv-thumb-index]');
      slides.forEach((el) => {
        el.classList.toggle('is-active', parseInt(el.dataset.qvSlideIndex, 10) === index);
      });
      thumbs.forEach((el) => {
        const active = parseInt(el.dataset.qvThumbIndex, 10) === index;
        el.classList.toggle('is-active', active);
        el.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    async open(handle) {
      if (!handle) return;

      this.dialog.classList.add('is-loading');
      if (this.body) this.body.innerHTML = '';
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      try {
        const html = await this.fetchProduct(handle);
        this.injectContent(html);
        this.dialog.classList.remove('is-loading');

        const closeBtn = this.dialog.querySelector('[data-qv-close]');
        if (closeBtn) closeBtn.focus({ preventScroll: true });
      } catch (err) {
        console.error('[QuickView] Failed to load product', handle, err);
        this.close();
        window.location.href = `${routeRoot()}products/${handle}`;
      }
    }

    async fetchProduct(handle) {
      // Use Shopify.routes.root so the fetch inherits the active storefront
      // locale prefix (e.g. /es/), and the response is rendered in that locale
      // (including translated metafields and i18n strings like "Add to cart").
      // Cache by full URL so a locale switch never serves stale HTML.
      const url = `${routeRoot()}products/${encodeURIComponent(handle)}?view=quick-view`;
      if (cache.has(url)) return cache.get(url);

      const res = await fetch(url, {
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      cache.set(url, html);
      return html;
    }

    injectContent(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector(CONTENT_SELECTOR);
      if (!content) throw new Error('Quick View content not found in response');
      if (this.body) {
        this.body.innerHTML = '';
        this.body.appendChild(content);
      }
    }

    reset() {
      this.dialog.classList.remove('is-loading');
      if (this.body) this.body.innerHTML = '';
    }

    close() {
      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
    }
  }

  if (!customElements.get('quick-view-dialog')) {
    customElements.define('quick-view-dialog', QuickViewDialog);
  }

  // Trigger: only the injected eye-icon button opens the Quick View.
  // The rest of the card behaves normally (link → PDP).
  document.addEventListener(
    'click',
    (e) => {
      const trigger = e.target.closest('.qv-trigger');
      if (!trigger) return;

      e.preventDefault();
      e.stopPropagation();

      const handle = trigger.dataset.qvHandle;
      if (!handle) return;

      const dialog = document.querySelector('quick-view-dialog');
      if (!dialog) return;

      dialog.open(handle);
    },
    { capture: true }
  );

  // Boot: inject triggers as soon as the DOM is ready, then watch for new cards.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      injectAll();
      observeNewCards();
    });
  } else {
    injectAll();
    observeNewCards();
  }
})();
